import React, { useEffect, useState } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import Loader from '../components/Loader';
import OwnerRegister from '../components/OwnerRegister'; // adjust path
import { Search, UserIcon, Phone, CheckCircle, AlertCircle, Calendar, Edit2, CheckCircleIcon } from 'lucide-react';



interface OwnerProperty {
  id: number;
  ownerId?: number;
  property_id?: number;
  propertyName: string;
  ownerName: string;
  address?: string;
  phone?: string;
  email?: string;
  status?: number;
}



const OwnerListings: React.FC = () => {
  const [properties, setProperties] = useState<OwnerProperty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);
  const [isAddOwnerOpen, setIsAddOwnerOpen] = useState(false);

  const formatDate = (date: Date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(formatDate(lastMonth));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  const [selectedProperty, setSelectedProperty] = useState<OwnerProperty | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editOwnerName, setEditOwnerName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);



  const navigate = useNavigate();

  const indexOfLastProperty = currentPage * itemsPerPage;
  const indexOfFirstProperty = indexOfLastProperty - itemsPerPage;
  const currentProperties = properties.slice(indexOfFirstProperty, indexOfLastProperty);
  const totalPages = Math.ceil(properties.length / itemsPerPage);

  const handleEditClick = (property: OwnerProperty) => {
    setSelectedProperty(property);
    setEditOwnerName(property.ownerName);
    setEditPhone(property.phone || '');
    setEditEmail(property.email || '');
    setIsEditModalOpen(true);
  };

  useEffect(() => {
    fetchOwnerProperties(startDate, endDate, searchTerm);
  }, []);

  const fetchOwnerProperties = async (start?: string, end?: string, search?: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.getOwners({
        startDate: start,
        endDate: end,
        search: search,
      });




      const ownerProperties: OwnerProperty[] = response.map((p: any) => ({
        id: p.id,
        ownerId: p.ownerId || p.owner_id || p.id,
        property_id: p.property_id,
        propertyName: p.property_name,
        ownerName: p.owner_name,
        phone: p.phone,
        email: p.email,
        address: p.address,
        status: p.status,
      }));




      setProperties(ownerProperties);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch owner listings');
    } finally {
      setIsLoading(false);
    }
  };

  //   if (!selectedProperty) return;

  //   try {
  //     await apiService.updateOwner(selectedProperty.id, {
  //       owner_name: editOwnerName,
  //       hotel_name: selectedProperty.propertyName,
  //       phone: editPhone,
  //       email: editEmail,
  //     });

  //     setIsEditModalOpen(false);
  //     fetchOwnerProperties(startDate, endDate, searchTerm);
  //   } catch (err) {
  //     console.error("Update Owner failed:", err);
  //     setError(err instanceof Error ? err.message : "Failed to update owner");
  //   }
  // };
  const handleUpdateOwner = async () => {
    if (!selectedProperty) return;

    try {
      await apiService.updateOwner({
        id: selectedProperty.ownerId ?? selectedProperty.id,
        name: editOwnerName,
        phone: editPhone,
        email: editEmail,
      });

      setIsEditModalOpen(false);

      // refresh list
      fetchOwnerProperties(startDate, endDate, searchTerm);

      // show success modal
      setSuccessMessage(`Owner "${editOwnerName}" updated successfully!`);
      setIsSuccessModalOpen(true);

    } catch (err) {
      console.error("Update Owner failed:", err);
      setError(err instanceof Error ? err.message : "Failed to update owner");
    }
  };



  return (
    <AdminLayout>
      <div className="px-6 py-4">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif font-bold text-sage-900">Owner Listings</h1>
            <p className="text-sm text-sage-500 mt-1">
              All properties submitted by owners
            </p>
          </div>

          <button
            onClick={() => setIsAddOwnerOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700 transition"
          >
            + Add Owner
          </button>

        </div>


        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white border border-beige-300 rounded-xl p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-400" />
              <input
                type="text"
                placeholder="Search by owner name or number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-beige-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-sage-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <button
              onClick={() => fetchOwnerProperties(startDate, endDate, searchTerm)}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700 transition"
            >
              Apply
            </button>

            <button
              onClick={() => {
                setSearchTerm('');
                setStartDate(formatDate(lastMonth));
                setEndDate(formatDate(today));
                fetchOwnerProperties(formatDate(lastMonth), formatDate(today), '');
              }}
              className="px-4 py-2 border border-beige-300 rounded-lg text-sm hover:bg-beige-50"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Records Per Page */}
        <div className="flex items-center gap-2 mb-4">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={18}>18</option>
            <option value={36}>36</option>
            <option value={54}>54</option>
            <option value={108}>108</option>
          </select>
          <span className="text-sm font-medium text-green-700">records per page</span>
        </div>

        {isLoading ? (
          <Loader fullScreen />
        ) : properties.length === 0 ? (
          <div className="bg-white rounded-xl border border-beige-300 p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-green-500" />
            </div>
            <p className="text-sage-900 text-lg font-semibold mb-1">No Listings Found</p>
            <p className="text-sage-500 text-sm">
              There are no properties submitted by owners yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentProperties.map((property) => (
              <div
                key={property.id}
                className="bg-white rounded-xl border border-beige-300 p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow duration-200 relative"
              >
                {/* Edit icon at top-right */}
                <div
                  onClick={() => handleEditClick(property)}
                  className="absolute top-3 right-3 p-2  rounded-lg cursor-pointer flex items-center justify-center"
                  title="Edit Owner"
                >
                  <Edit2 size={16} className="text-gray-400" />
                </div>

                <div className="flex items-center gap-2">
                  <UserIcon size={22} className="text-brand-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-sage-900">{property.propertyName}</p>
                    <p className="text-xs text-sage-500 mt-0.5">{property.ownerName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-sage-400 mt-2">
                  <Phone size={12} />
                  <span>{property.phone || 'No phone provided'}</span>
                </div>

                <button
                  onClick={() => navigate(`/hotels?ownerId=${property.ownerId ?? property.id}`)


                  }

                  className="mt-3 px-3 py-1 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors"
                >
                  View Properties
                </button>
              </div>

            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded-lg text-sm ${currentPage === i + 1 ? 'bg-brand-600 text-white' : 'bg-white'
                  }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Edit Owner Modal */}
        {isEditModalOpen && selectedProperty && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4">Edit Owner Details</h2>
              <div className="space-y-3">
                <input
                  type="text"
                  value={editOwnerName}
                  onChange={(e) => setEditOwnerName(e.target.value)}
                  placeholder="Owner Name"
                  className="w-full border rounded-lg px-3 py-2"
                />
                <input
                  type="text"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="Phone"
                  maxLength={9}
                  className="w-full border rounded-lg px-3 py-2"
                />

                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full border rounded-lg px-3 py-2"
                />

              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateOwner}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg"
                >
                  Update
                </button>

              </div>
            </div>
          </div>
        )}
        {isSuccessModalOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm text-center">
              <CheckCircleIcon size={48} className="mx-auto mb-4 text-green-500" />

              <h2 className="text-lg font-semibold mb-2">Update Successful</h2>
              <p className="mb-5">{successMessage}</p>
              <button
                onClick={() => setIsSuccessModalOpen(false)}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
      {isAddOwnerOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg relative">

            {/* Close button */}
            <button
              onClick={() => setIsAddOwnerOpen(false)}
              className="absolute top-3 right-3 text-gray-500"
            >
              ✕
            </button>

            <OwnerRegister
              onSuccess={() => {
                setIsAddOwnerOpen(false);
                fetchOwnerProperties(startDate, endDate, searchTerm);
              }}
              isOpen={isAddOwnerOpen}
              onClose={() => setIsAddOwnerOpen(false)}
            />
          </div>
        </div>
      )}

    </AdminLayout>
  );
};

export default OwnerListings;
